import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../data/mockData';
import { fetchUserOrders } from '../services/api';
import { ArrowLeft, ShoppingBag, CheckCircle2, Package, Truck, Home, XCircle, ChevronDown, ChevronUp, Store } from 'lucide-react';

const STATUS_CFG = {
  confirmed: { color: '#2196F3', icon: CheckCircle2, label: 'Confirmed' },
  processing: { color: '#FF9800', icon: Package, label: 'Processing' },
  shipped: { color: '#7E57C2', icon: Truck, label: 'Shipped' },
  delivered: { color: '#4CAF50', icon: Home, label: 'Delivered' },
  cancelled: { color: '#EF5350', icon: XCircle, label: 'Cancelled' },
};

export default function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetchUserOrders().then(setOrders).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: COLORS.textSecondary }}>Loading orders...</div>;

  return (
    <div data-testid="orders-page" style={{ padding: '24px 20px', maxWidth: 800, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><ArrowLeft size={22} color={COLORS.textPrimary} /></button>
        <ShoppingBag size={26} color={COLORS.primary} />
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>My Orders</h1>
      </div>

      {orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 64 }}>
          <ShoppingBag size={56} color={COLORS.border} />
          <h3 style={{ fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, marginTop: 16, fontFamily: 'Outfit, sans-serif' }}>No Orders Yet</h3>
          <p style={{ fontSize: 14, color: COLORS.textSecondary, marginTop: 8 }}>Your order history will appear here after your first purchase.</p>
          <button data-testid="orders-shop-btn" onClick={() => navigate('/dashboard/store')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 20, padding: '12px 24px', borderRadius: 9999, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}><Store size={16} /> Browse Store</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {orders.map((order, idx) => {
            const isExp = expanded === (order.order_id || idx);
            const cfg = STATUS_CFG[order.status] || STATUS_CFG.confirmed;
            const StatusIcon = cfg.icon;
            return (
              <div key={order.order_id || idx} data-testid={`order-card-${idx}`} style={{ backgroundColor: COLORS.surface, borderRadius: 16, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', cursor: 'pointer' }} onClick={() => setExpanded(isExp ? null : (order.order_id || idx))}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>Order #{(order.order_id || '').slice(-8).toUpperCase()}</div>
                    <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 3 }}>{order.created_at ? new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}</div>
                  </div>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 9999, backgroundColor: `${cfg.color}18`, color: cfg.color, fontSize: 12, fontWeight: 700 }}><StatusIcon size={14} />{cfg.label}</span>
                </div>
                <div style={{ borderTop: `1px solid ${COLORS.border}30`, paddingTop: 10, marginBottom: 10 }}>
                  {(order.items || []).map((item, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                      <span style={{ flex: 1, fontSize: 13, color: COLORS.textPrimary }}>{item.product_name || item.name}</span>
                      <span style={{ fontSize: 12, color: COLORS.textSecondary, marginRight: 12 }}>x{item.quantity}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary, minWidth: 60, textAlign: 'right' }}>${((item.product_price || item.price || 0) * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${COLORS.border}30`, paddingTop: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.textSecondary }}>Total</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: COLORS.primary }}>${(order.total || 0).toFixed(2)}</span>
                </div>
                {isExp && (
                  <div style={{ marginTop: 14, borderTop: `1px solid ${COLORS.border}30`, paddingTop: 14 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 8 }}>Order Details</div>
                    <div style={{ fontSize: 13, color: COLORS.textSecondary }}>Order ID: {order.order_id}</div>
                    <div style={{ fontSize: 13, color: COLORS.textSecondary }}>Status: {cfg.label}</div>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 10 }}>
                  {isExp ? <ChevronUp size={16} color={COLORS.textSecondary} /> : <ChevronDown size={16} color={COLORS.textSecondary} />}
                  <span style={{ fontSize: 12, color: COLORS.textSecondary }}>{isExp ? 'Hide details' : 'View details'}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
